// frontend/src/modules/admin/components/GestionHomePage.jsx
// Gestion paramètres HomePage (HR_settings) - La Ferme du Petit Essart
// 73+ Papy - AVEC BOUTON UPLOAD comme GestionProduits !

import React, { useState, useEffect } from 'react';
import { 
  Image, 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Settings, 
  Save, 
  Loader,
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Upload
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const GestionHomePage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);

  // Chargement des settings HR_settings
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/settings`);
      if (!response.ok) throw new Error('Erreur chargement');
      const data = await response.json();
      setSettings(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur:', error);
      setMessage({ type: 'error', text: '❌ Erreur chargement des paramètres' });
      setLoading(false);
    }
  };

  // Sauvegarde des settings
  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${API_BASE}/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: '✅ Paramètres sauvegardés !' });
        // Effacer le message après 3 secondes
        setTimeout(() => setMessage(null), 3000);
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: `❌ ${errorData.message || 'Erreur sauvegarde'}` });
      }
    } catch (error) {
      console.error('Erreur:', error);
      setMessage({ type: 'error', text: '❌ Erreur serveur' });
    } finally {
      setSaving(false);
    }
  };

  // 🆕 UPLOAD IMAGE/VIDEO HERO (comme GestionProduits)
  const handleUploadHero = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: '❌ Fichier trop volumineux ! Maximum 10MB' });
      return;
    }
    
    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('media', file);
    
    try {
      // Utiliser la même route que les produits (même répertoire)
      const response = await fetch(`${API_BASE}/site/upload-produit-media`, {
        method: 'POST',
        body: uploadFormData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSettings(prev => ({
          ...prev, 
          hero_media_url: data.url
        }));
        setMessage({ type: 'success', text: '✅ Image uploadée avec succès !' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: `❌ ${data.message}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: `❌ Erreur upload : ${error.message}` });
    } finally {
      setUploading(false);
    }
  };

  // Mise à jour d'un champ
  const updateField = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-gray-600" />
        <span className="ml-3 text-gray-600">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">🏠 Gestion Page d'Accueil</h2>
          <p className="text-sm text-gray-500 mt-1">
            Paramètres affichés sur la HomePage publique
          </p>
        </div>
        <div className="flex items-center gap-3">
          <a 
            href="/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            <ExternalLink className="w-4 h-4" />
            Voir le site
          </a>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Sauvegarder
              </>
            )}
          </button>
        </div>
      </div>

      {/* Message de feedback */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertTriangle className="w-5 h-5" />
          )}
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* 📢 Section Hero/Bannière */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Image className="w-5 h-5 text-purple-600" />
            Bannière d'accueil
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Texte de la bannière (headline)
              </label>
              <input
                type="text"
                value={settings?.hero_headline || ''}
                onChange={(e) => updateField('hero_headline', e.target.value)}
                className="w-full p-3 border rounded-md focus:ring-purple-500 focus:border-purple-500"
                placeholder="Bienvenue à La Ferme du Petit Essart"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de média
              </label>
              <select
                value={settings?.hero_type || 'image'}
                onChange={(e) => updateField('hero_type', e.target.value)}
                className="w-full p-3 border rounded-md focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="image">Image</option>
                <option value="video">Vidéo</option>
              </select>
            </div>

            {/* 🆕 BOUTON UPLOAD IMAGE/VIDEO */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📸 Image/Vidéo de la bannière
              </label>
              
              <div className="mb-3">
                <input
                  type="file"
                  id="hero-upload"
                  accept="image/*,video/*"
                  onChange={handleUploadHero}
                  className="hidden"
                />
                <label
                  htmlFor="hero-upload"
                  className={`inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md cursor-pointer hover:bg-blue-700 ${
                    uploading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {uploading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Upload en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      📤 Télécharger une image/vidéo
                    </>
                  )}
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL du média
                </label>
                <input
                  type="text"
                  value={settings?.hero_media_url || ''}
                  onChange={(e) => updateField('hero_media_url', e.target.value)}
                  className="w-full p-3 border rounded-md focus:ring-purple-500 focus:border-purple-500"
                  placeholder="/uploads/produits/hero.webp"
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Ou saisir une URL manuelle
                </p>
              </div>
            </div>

            {/* Prévisualisation */}
            {settings?.hero_media_url && (
              <div className="border rounded-lg p-3 bg-gray-50">
                <p className="text-xs text-gray-500 mb-2">Aperçu :</p>
                {settings?.hero_type === 'video' ? (
                  <video 
                    src={settings.hero_media_url} 
                    className="w-full h-32 object-cover rounded"
                    muted
                    controls
                  />
                ) : (
                  <img 
                    src={settings.hero_media_url} 
                    alt="Preview hero"
                    className="w-full h-32 object-cover rounded"
                    onError={(e) => {
                      e.target.src = '/placeholder.svg';
                      e.target.alt = 'Image non trouvée';
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* 📍 Section Coordonnées */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-red-600" />
            Coordonnées
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse
              </label>
              <input
                type="text"
                value={settings?.adresse || ''}
                onChange={(e) => updateField('adresse', e.target.value)}
                className="w-full p-3 border rounded-md focus:ring-red-500 focus:border-red-500"
                placeholder="Rue Champ du Bois, 76530 Grand-Couronne"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Mail className="w-4 h-4" />
                Email de contact
              </label>
              <input
                type="email"
                value={settings?.contact_email || ''}
                onChange={(e) => updateField('contact_email', e.target.value)}
                className="w-full p-3 border rounded-md focus:ring-red-500 focus:border-red-500"
                placeholder="contact@lafermedupetitessart.fr"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Phone className="w-4 h-4" />
                Téléphone
              </label>
              <input
                type="tel"
                value={settings?.contact_phone || ''}
                onChange={(e) => updateField('contact_phone', e.target.value)}
                className="w-full p-3 border rounded-md focus:ring-red-500 focus:border-red-500"
                placeholder="06 31 15 62 35"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Horaires d'ouverture
              </label>
              <input
                type="text"
                value={settings?.opening_hours || ''}
                onChange={(e) => updateField('opening_hours', e.target.value)}
                className="w-full p-3 border rounded-md focus:ring-red-500 focus:border-red-500"
                placeholder="Samedi 09H00-12H00"
              />
            </div>
          </div>
        </div>

        {/* 🗺️ Section Carte OpenStreetMap */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            🗺️ Carte OpenStreetMap
          </h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Latitude GPS
                </label>
                <input
                  type="text"
                  value={settings?.gps_latitude || ''}
                  onChange={(e) => updateField('gps_latitude', e.target.value)}
                  className="w-full p-3 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="49.3352469940805"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Longitude GPS
                </label>
                <input
                  type="text"
                  value={settings?.gps_longitude || ''}
                  onChange={(e) => updateField('gps_longitude', e.target.value)}
                  className="w-full p-3 border rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="1.026987920131098"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Niveau de zoom (1-20)
              </label>
              <input
                type="number"
                value={settings?.gps_zoom || '17'}
                onChange={(e) => updateField('gps_zoom', e.target.value)}
                min="1"
                max="20"
                className="w-full p-3 border rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                💡 <strong>Trouver vos coordonnées :</strong>
              </p>
              <a 
                href="https://www.openstreetmap.org/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1"
              >
                <ExternalLink className="w-3 h-3" />
                OpenStreetMap.org
              </a>
            </div>
          </div>
        </div>

        {/* 🔧 Section Mode maintenance */}
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-orange-600" />
            Mode maintenance
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                État du site
              </label>
              <select
                value={settings?.etat_site || 'ouvert'}
                onChange={(e) => updateField('etat_site', e.target.value)}
                className="w-full p-3 border rounded-md focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="ouvert">✅ Ouvert (mode normal)</option>
                <option value="maintenance">🔧 Maintenance (site fermé)</option>
              </select>
            </div>

            {settings?.etat_site === 'maintenance' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Titre de maintenance
                  </label>
                  <input
                    type="text"
                    value={settings?.maintenance_text || ''}
                    onChange={(e) => updateField('maintenance_text', e.target.value)}
                    className="w-full p-3 border rounded-md"
                    placeholder="🔧 Site en maintenance"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message de maintenance
                  </label>
                  <textarea
                    value={settings?.maintenance_message || ''}
                    onChange={(e) => updateField('maintenance_message', e.target.value)}
                    className="w-full p-3 border rounded-md h-24"
                    placeholder="Nous effectuons des améliorations..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Image de maintenance
                  </label>
                  <input
                    type="text"
                    value={settings?.maintenance_image_url || ''}
                    onChange={(e) => updateField('maintenance_image_url', e.target.value)}
                    className="w-full p-3 border rounded-md"
                    placeholder="/uploads/images/maintenance.webp"
                  />
                </div>
              </>
            )}

            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-800">
                ⚠️ <strong>En mode maintenance</strong>, seules les pages admin restent accessibles.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GestionHomePage;